import { combineLatest, Observable, of } from 'rxjs';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { platformOrder } from '@app/configurations';
import { CustomerSubscriptionApiService } from '@app/core/api/customer-subscription-api.service';
import {
  AdspertPlanType,
  BillingVolume,
  CustomerSubscription,
  InvoiceCurrency,
  PlanConstraints,
  SubscriptionStatus,
} from '@app/core/models/customer.models';
import { assertArray } from '@app/core/utilities/array.utilities';
import {
  hasActiveSubscription,
  isLegacySubscription,
  isManagedSubscription,
  isPrimordialSubscription,
} from '@app/settings/shared/modules/subscription/subscription.utilities';
import { defaultToastError } from '@app/shared/operators/default-toast-error.operator';
import { ReloadCustomer } from '../customer-state.actions';
import { CustomerState } from '../customer.state';
import {
  CancelCustomerSubscription,
  DeleteCustomerSubscription,
  GetCustomerSubscription,
  GetCustomerSubscriptionConstraints,
  PreviewCustomerSubscription,
  RestartCustomerSubscription,
  StartCustomerSubscription,
} from './subscription-state.actions';
import { CUSTOMER_SUBSCRIPTION_STATE_TOKEN, SubscriptionStateModel } from './subscription-state.models';

type ThisStateCtx = StateContext<SubscriptionStateModel>;

@Injectable()
@State<SubscriptionStateModel>({
  name: CUSTOMER_SUBSCRIPTION_STATE_TOKEN,
  defaults: {},
})
export class SubscriptionState {
  @Selector()
  static subscription({ details }: SubscriptionStateModel): CustomerSubscription {
    return details;
  }

  @Selector()
  static preview({ preview }: SubscriptionStateModel): CustomerSubscription[] {
    return assertArray(preview);
  }

  @Selector()
  static planTypes({ constraints }: SubscriptionStateModel): AdspertPlanType[] {
    return assertArray(constraints?.types);
  }

  @Selector([SubscriptionState.planTypes])
  static isSelfServiceLegacy(planTypes: AdspertPlanType[]): boolean {
    return planTypes.includes(AdspertPlanType.Standard);
  }

  @Selector()
  static isEssentialPlan({ details }: SubscriptionStateModel): boolean {
    return !details?.type || details.type === AdspertPlanType.Essential;
  }

  @Selector()
  static currency({ details }: SubscriptionStateModel): InvoiceCurrency {
    return details?.currency || 'EUR';
  }

  @Selector()
  static checkoutCurrencies({ constraints }: SubscriptionStateModel): InvoiceCurrency[] {
    return assertArray(constraints?.currencies || 'EUR');
  }

  @Selector()
  static minEndDate({ constraints }: SubscriptionStateModel): Date {
    return new Date(constraints?.minEndDate || Date.now());
  }

  @Selector()
  static checkoutCurrency({ details, constraints }: SubscriptionStateModel): InvoiceCurrency {
    return details?.currency || constraints?.currencies?.[0] || 'EUR';
  }

  @Selector([SubscriptionState.subscription])
  static billingVolume(subscription: CustomerSubscription): BillingVolume[] {
    const data = [...subscription?.billingVolume];
    const sortedData = data.sort((a, b) => {
      const aMinIndex = Math.min(...a.platforms.map((p) => platformOrder.indexOf(p)));
      const bMinIndex = Math.min(...b.platforms.map((p) => platformOrder.indexOf(p)));
      return aMinIndex - bMinIndex;
    });

    return sortedData;
  }

  @Selector()
  static hasSubscription({ details }: SubscriptionStateModel): boolean {
    return !!details?.status;
  }

  @Selector()
  static hasValidSubscription({ details }: SubscriptionStateModel): boolean {
    return !!details?.status && details?.status !== SubscriptionStatus.Terminated;
  }

  @Selector()
  static hasActiveSubscription({ details }: SubscriptionStateModel): boolean {
    return hasActiveSubscription(details);
  }

  @Selector()
  static isManagedSubscription({ details }: SubscriptionStateModel): boolean {
    return isManagedSubscription(details);
  }

  @Selector()
  static isLegacySubscription({ details }: SubscriptionStateModel): boolean {
    return isLegacySubscription(details);
  }

  @Selector()
  static isPrimordialSubscription({ details }: SubscriptionStateModel): boolean {
    return isPrimordialSubscription(details);
  }

  @Selector([SubscriptionState.subscription])
  static endDate(subscription: CustomerSubscription): Date | undefined {
    return subscription?.endDate && new Date(subscription.endDate);
  }

  constructor(
    private store: Store,
    private cs: CustomerSubscriptionApiService,
  ) {}

  @Action(GetCustomerSubscription)
  getCustomerSubscription({ setState }: ThisStateCtx): Observable<unknown> {
    return this.cs.getCustomerSubscription().pipe(
      map((response) => response?.data),
      catchError(() => of(null)),
      tap((details: CustomerSubscription) => setState(patch({ details }))),
    );
  }

  @Action(GetCustomerSubscriptionConstraints)
  getCustomerSubscriptionConstraints({ setState }: ThisStateCtx): Observable<unknown> {
    return this.cs.getCustomerPlanConstraints().pipe(
      map((response) => response?.data),
      catchError(() => of(null)),
      tap((constraints: PlanConstraints) => setState(patch({ constraints }))),
    );
  }

  @Action(StartCustomerSubscription)
  startCustomerSubscription(
    { setState, dispatch }: ThisStateCtx,
    { type, currency, period }: StartCustomerSubscription,
  ): Observable<unknown> {
    const hasSubscription = this.store.selectSnapshot(SubscriptionState.hasValidSubscription);
    const checkoutCurrency = currency || this.store.selectSnapshot(SubscriptionState.checkoutCurrency);

    return this.cs.startCustomerSubscription(type, checkoutCurrency, false, hasSubscription, period).pipe(
      map((response) => response.data),
      defaultToastError(),
      tap((details: CustomerSubscription) => setState(patch({ details }))),
      delay(1000),
      switchMap(() => dispatch([new ReloadCustomer(), new GetCustomerSubscriptionConstraints()])),
    );
  }

  @Action(CancelCustomerSubscription)
  cancelCustomerSubscription({ setState }: ThisStateCtx, { endDate }: CancelCustomerSubscription): Observable<unknown> {
    const subscription = this.store.selectSnapshot(SubscriptionState.subscription);

    return this.cs.cancelCustomerSubscription(subscription.type || AdspertPlanType.Essential, endDate).pipe(
      map((response) => response.data),
      tap((details: CustomerSubscription) => setState(patch({ details }))),
    );
  }

  @Action(DeleteCustomerSubscription)
  deleteCustomerSubscription({ setState, dispatch }: ThisStateCtx): Observable<unknown> {
    return this.cs.deleteCustomerSubscription().pipe(
      tap(() => setState(patch({ details: null }))),
      switchMap(() => dispatch(new GetCustomerSubscriptionConstraints())),
    );
  }

  @Action(RestartCustomerSubscription)
  restartCustomerSubscription({ setState, dispatch }: ThisStateCtx): Observable<unknown> {
    const subscription = this.store.selectSnapshot(SubscriptionState.subscription);

    return this.cs.restartCustomerSubscription(subscription.type || AdspertPlanType.Essential).pipe(
      map((response) => response.data),
      tap((details) => setState(patch({ details }))),
      switchMap(() => dispatch([new ReloadCustomer(), new GetCustomerSubscriptionConstraints()])),
    );
  }

  @Action(PreviewCustomerSubscription)
  previewCustomerSubscription(
    { setState }: ThisStateCtx,
    { currency }: PreviewCustomerSubscription,
  ): Observable<unknown> {
    const hasSubscription = this.store.selectSnapshot(SubscriptionState.hasValidSubscription);
    const planTypes = this.store.selectSnapshot(SubscriptionState.planTypes);
    const customerIsAgency = this.store.selectSnapshot(CustomerState.customerIsAgency);

    return combineLatest(
      planTypes.map((planType) =>
        this.cs
          .startCustomerSubscription(planType, currency, true, hasSubscription)
          .pipe(map((response) => response.data)),
      ),
    ).pipe(
      defaultToastError(),
      tap((preview: CustomerSubscription[]) =>
        setState(
          patch({
            preview: preview
              .sort((a, b) => a.pricingDetails.fixedFee - b.pricingDetails.fixedFee)
              .map((plan) => ({
                ...plan,
                unavailable: customerIsAgency && plan.type === AdspertPlanType.Essential,
              })),
          }),
        ),
      ),
    );
  }
}
